import Vue from 'vue'
import AgGridColumnMethodMixins from '@/Mixins/SigmaClientNew/AgGridColumnMethods'
import { isServerFiltered, statusFilterValues, offeredSourceCodes, PAYMENT_LIST_STATUS_LABELS, PAYMENT_STATUS_VALUES } from '@/Mixins/SigmaClientNew/transactionGridFilters'

// import { getSimpleDate } from '@/utils/helpers/FormatDate'
/*
* AG Grid Column Defs here
*/
export default Vue.extend({
  mixins: [AgGridColumnMethodMixins],
  data () {
    /**
     * On the server filtered views the API has already applied the column criteria to the page the
     * grid was handed, so the grid must not evaluate those criteria a second time - a row the API
     * matched on another field (the keyword search spans several) would otherwise be hidden.
     */
    const serverFiltered = isServerFiltered(this.$route.name)

    const uniqueValues = (values) => [...new Set(values.filter(v => v !== null && v !== undefined && v !== ''))]

    const textFilterParams = () => {
      const params = { suppressAndOrCondition: true }
      if (serverFiltered) params.textCustomComparator = () => true
      return params
    }

    const serverSetFilterValue = (getValues) => serverFiltered ? { filterValueGetter: () => getValues() } : {}

    /**
     * The text columns are filtered from their floating filter, which applies what was typed when
     * the user asks for it. Typing goes on to hit the API, so it is not applied on a timer.
     */
    const textFloatingFilter = {
      floatingFilter: true,
      floatingFilterComponent: 'agGridTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true
      }
    }

    /**
     * Text and date filters drop rows with an empty cell value before they ever reach the
     * comparator, so server side they are handed a value that keeps every row in.
     */
    const serverFilterValue = serverFiltered ? { filterValueGetter: () => '' } : {}

    // Server side only the criteria the API understands are offered: an exact date, or a range
    const dateFilterParams = (comparator) => {
      if (serverFiltered) {
        return {
          suppressAndOrCondition: true,
          filterOptions: ['equals'],
          inRangeInclusive: false,
          comparator: () => 0
        }
      }
      const params = { suppressAndOrCondition: true }
      if (comparator) params.comparator = comparator
      return params
    }

    /**
     * Set filter values are read when the filter is opened so they follow the data currently loaded.
     * The server query is built from the same values, so what the grid keeps and what the API
     * returns stay in agreement.
     */
    const setFilterParams = (getValues, { valueFormatter } = {}) => {
      const params = {
        suppressAndOrCondition: true,
        values: (params) => params.success(uniqueValues(getValues())),
        refreshValuesOnOpen: true,
        /**
         * The selection survives the rows being replaced. With the client side row model ag-grid
         * otherwise clears it every time new rows arrive, which on these views means after every
         * fetch the filter itself triggered - it would drop what the user just picked.
         */
        newRowsAction: 'keep',
        /**
         * The values are read in the order they are given - the statuses in the order a
         * transaction goes through them, say. Left to itself the filter sorts them, and with no
         * comparator of its own it borrows the column's, which is there for sorting rows.
         */
        suppressSorting: true,
        buttons: ['apply', 'cancel'],
        // Both buttons are the end of picking values, so they put the popup away
        closeOnApply: true
      }
      // Filters whose stored value is not what the user should read, e.g. payment types
      if (valueFormatter) params.valueFormatter = valueFormatter
      return params
    }

    // Names/codes of everything of a kind held in the store, e.g. every vendor on the loaded pages
    const storeValues = (getter, attribute) => () => uniqueValues((this.$store.getters[getter] || []).map(v => v && v[attribute]))
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))

    // The codes the source code filter offers, which is where the payroll one is left out
    const sourceCodeValues = () => offeredSourceCodes(storeValues('getAllSourcecodes', 'code')())

    // The statuses this view is scoped to (open/posted/all tab), falling back to every status
    const statusValues = () => statusFilterValues(this.$route.name, this.baseQuery)

    // Payments are read as printed rather than posted; everywhere else a status reads as it is
    const statusLabel = (params) => PAYMENT_LIST_STATUS_LABELS[params.value] || params.value

    const paymentTypeLabels = {
      CHECK: 'Check',
      WIRE: 'Wire Transfer',
      EFT: 'EFT',
      CHECK_MANUAL: 'Manual Check',
      NA: 'N/A'
    }

    return {
      rowSelect: {
        field: 'selection',
        maxWidth: 40,
        minWidth: 40,
        headerCheckboxSelection: true,
        checkboxSelection: true
      },
      ledgerColumn: {
        headerName: 'ID',
        field: 'ledger',
        colId: 'ledger',
        maxWidth: 150,
        minWidth: 100,
        sortable: true,
        sort: 'desc',
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        cellRenderer: (params) => params.data ? this.buildLink(params) : '',
        valueSetter: () => null
      },
      idColumn: {
        headerName: 'ID',
        field: 'id',
        colId: 'id',
        maxWidth: 150,
        minWidth: 100,
        sortable: true,
        sort: 'desc',
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        cellRenderer: (params) => params.data ? this.buildLink(params) : '',
        valueGetter: (params) => {
          return (params.data) ? `PO${params.data.id}` : ''
        },
        valueSetter: () => null
      },
      // Simulated Column
      numberColumn: {
        headerName: 'Number',
        field: 'number',
        colId: 'number',
        maxWidth: 250,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        cellRenderer: (params) => params.data ? this.buildLink(params) : '',
        valueSetter: () => null
      },
      // Simulated Column
      vendorColumn: {
        headerName: 'Vendor',
        field: 'vendor',
        colId: 'vendor',
        maxWidth: 250,
        minWidth: 150,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Vendors are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchVendorsWithQuery',
          entityKey: 'vendors',
          placeholder: 'Search vendors',
          emptyText: 'Search for a vendor to filter by'
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Vendor'
        },
        cellStyle: this.getStyles('vendor'),
        valueGetter: (params) => {
          if (params.data) {
            const vendor = params.data.vendor_data || params.data.vendor
            return vendor ? vendor.name : ''
          } else {
            return ''
          }
        },
        valueSetter: () => null
      },
      // Simulated Column
      descriptionColumn: {
        headerName: 'Description',
        field: 'description',
        colId: 'description',
        maxWidth: 400,
        minWidth: 300,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('description'),
        valueGetter: (params) => params.data ? params.data.description : '',
        valueSetter: () => null
      },
      amountColumn: {
        headerName: 'Amount',
        field: 'amount_decimal',
        colId: 'amount_decimal',
        maxWidth: 200,
        minWidth: 120,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        type: 'rightAligned',
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => {
          return (params.data && params.data.amount_decimal) ? Number(params.data.amount_decimal).toFixed(2) : '0.00'
        },
        valueSetter: () => null
      },
      envelopeAmountColumn: {
        headerName: 'Amount',
        field: 'amount_decimal',
        colId: 'amount_decimal',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        type: 'rightAligned',
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => {
          return (params.data && params.data.amount_decimal) ? Number(params.data.amount_decimal).toFixed(2) : '0.00'
        },
        valueSetter: () => null
      },
      debitsAmountColumn: {
        headerName: 'Amount',
        field: 'debits_decimal',
        colId: 'debits_decimal',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        type: 'rightAligned',
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => (params.data && params.data.debits_decimal) ? Number(params.data.debits_decimal).toFixed(2) : '',
        valueSetter: () => null
      },
      debitsDecimalAmountColumn: {
        headerName: 'Amount',
        field: 'amount',
        colId: 'amount',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        type: 'rightAligned',
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => params.data ? Number(params.data.amount_decimal || params.data.debits_decimal || 0).toFixed(2) : '',
        valueSetter: () => null
      },
      originalAmountColumn: {
        headerName: 'Original Amount',
        field: 'amount_decimal',
        colId: 'amount_decimal',
        type: 'rightAligned',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => (params.data && params.data.amount_decimal) ? Number(params.data.amount_decimal).toFixed(2) : '',
        valueSetter: () => null
      },
      remainingAmountColumn: {
        headerName: 'Remaining',
        field: 'remaining',
        colId: 'remaining',
        maxWidth: 200,
        type: 'rightAligned',
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        cellStyle: this.getStyles('amount'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueFormatter: (params) => this.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => (params.data && params.data.remaining) ? Number(params.data.remaining).toFixed(2) : '',
        valueSetter: () => null
      },
      dateColumn: {
        headerName: 'Date',
        field: 'date',
        colId: 'date',
        maxWidth: 100,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterParams: dateFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles('date'),
        comparator: (date1, date2) => {
          const dateObj1 = new Date(date1)
          const dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        },
        valueFormatter: (params) => {
          if (params.data && params.data.date) {
            const weekDate = params.data.date
            const weekDateList = weekDate.split('-')
            return weekDateList[1] + '-' + weekDateList[2].slice(0, 2) + '-' + weekDateList[0] // MM/DD/YYYY
          }
          return null
        },
        valueGetter: (params) => params.data ? params.data.date : '',
        valueSetter: () => null
      },
      sourceCodeColumn: {
        headerName: 'Source Code',
        field: 'source_code',
        colId: 'source_code',
        maxWidth: 100,
        minWidth: 80,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        // The DC and reversal codes are offered here too, unticked until the user asks for them
        filterParams: setFilterParams(sourceCodeValues),
        /**
         * Server side the API has already applied the codes to the page the grid was handed, so
         * every row it returned is one to show - the journals under no source code among them,
         * which a set filter would otherwise drop for matching none of the codes it is asking for.
         * The value the filter matches on is read from what it is asking for, so each row passes.
         */
        ...(serverFiltered ? {
          filterValueGetter: (params) => {
            const model = params.api.getFilterModel()['source_code']
            const values = model && model.values
            return (values && values.length) ? values[0] : ''
          }
        } : {}),
        cellStyle: this.getStyles('source_code'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        // A row under no source code - or under one that is not among the loaded ones - reads blank
        valueGetter: (params) => {
          if (!(params.data && params.data.source_code)) return ''
          const sourcecode = (this.$store.getters.getAllSourcecodes || []).find(s => s.id === params.data.source_code)
          return sourcecode ? sourcecode.code : ''
        },
        valueSetter: () => null
      },
      batchColumn: {
        headerName: 'Batch',
        field: 'batch',
        colId: 'batch',
        maxWidth: 300,
        minWidth: 200,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Batches are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchBatchesWithQuery',
          entityKey: 'batches',
          placeholder: 'Search batches',
          emptyText: 'Search for a batch to filter by'
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Batch'
        },
        cellStyle: this.getStyles('batch'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => (params.data && params.data.batch_data) ? params.data.batch_data.name : '',
        valueSetter: () => null
      },
      currencyColumn: {
        headerName: 'Currency',
        field: 'currency',
        colId: 'currency',
        maxWidth: 80,
        minWidth: 80,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: setFilterParams(storeValues('getAllCurrencies', 'code')),
        cellStyle: this.getStyles('currency'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => params.data && params.data.currency_data ? params.data.currency_data.code : '',
        valueSetter: () => null
      },
      paymentTypeColumn: {
        headerName: 'Payment Type',
        field: 'payment_type',
        colId: 'payment_type',
        maxWidth: 200,
        minWidth: 100,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        // The stored payment types are filtered on, the labels are only what the user reads
        filterParams: setFilterParams(
          () => Object.keys(paymentTypeLabels),
          { valueFormatter: (params) => paymentTypeLabels[params.value] || params.value }
        ),
        ...serverSetFilterValue(() => Object.keys(paymentTypeLabels)),
        cellStyle: this.getStyles('payment_type'),
        valueGetter: (params) => {
          if (!params.data) return ''
          return paymentTypeLabels[params.data.payment_type] || params.data.payment_type || 'N/A'
        },
        valueSetter: () => null
      },
      paymentNumberColumn: {
        headerName: 'Payment Number',
        field: 'payment_confirmation_number',
        colId: 'payment_confirmation_number',
        maxWidth: 200,
        minWidth: 120,
        sortable: true,
        wrapText: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverFilterValue,
        cellStyle: this.getStyles(),
        valueSetter: () => null
      },
      paymentStatusColumn: {
        field: 'Payment Status',
        colId: 'payment_processed',
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: {
          suppressAndOrCondition: true,
          // Read and picked as Cleared/Pending; the query turns them into the flag behind them
          values: Object.keys(PAYMENT_STATUS_VALUES),
          newRowsAction: 'keep',
          // Read in the order they are given rather than sorted
          suppressSorting: true,
          buttons: ['apply', 'cancel'],
          closeOnApply: true
        },
        cellStyle: this.getStyles('payment_processed'),
        // The label the filter matches on, sorts by and exports
        valueGetter: (params) => {
          if (!params.data) return ''
          return params.data.payment_processed ? 'PAID' : 'UNPAID'
        },
        /**
         * ag-grid puts this markup straight into the cell, so it has to be the button element-ui
         * would have rendered: an <el-button> here is never compiled by Vue and would show up as
         * its own tag text.
         */
        cellRenderer: (params) => {
          if (!params.data) return ''
          return `<div class="el-tag" style="${params.data.payment_processed && params.data.payment_processed === true ? 'background-color: #13ce66;' : 'background-color: #ffffff; border-color: #d1dbe5; color: #282828;'}">${params.value}</div>`
        },
        minWidth: 100
      },
      actionsColumn:
      {
        field: 'Status',
        colId: 'status',
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: setFilterParams(statusValues, { valueFormatter: this.$route.name === 'Payments' ? statusLabel : undefined }),
        cellRenderer: 'agGridBtnRender',
        cellStyle: this.getStyles('actions'),
        // The renderer works off params.data, the value is here so the filter has something to match
        valueGetter: (params) => params.data ? params.data.status : '',
        minWidth: 140
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/Mixins/SigmaClientNew/AgGridTransactionsColumns.js